import type RealtimeKitClient from "@cloudflare/realtimekit";

export type CameraFacing = "user" | "environment";

const FACING_KEY = "constela-camera-facing";
const BACK_LABEL = /back|rear|traseir|environment/i;
const FRONT_LABEL = /front|frontal|selfie|facetime|user/i;
const EXTRA_LENS_LABEL = /ultra|wide|angular|tele|dual|dupla|triple|tripla/i;

type CameraSelf = {
  videoEnabled?: boolean;
  getVideoDevices?: () => Promise<MediaDeviceInfo[]>;
  getCurrentDevices?: () => { video?: MediaDeviceInfo };
  setDevice?: (device: MediaDeviceInfo) => Promise<void>;
  on?: (event: string, handler: (payload: { device?: MediaDeviceInfo }) => void) => void;
  removeListener?: (event: string, handler: (payload: { device?: MediaDeviceInfo }) => void) => void;
};

export function isMobileCameraBrowser(): boolean {
  return typeof navigator !== "undefined" && /android|iphone|ipad|ipod/i.test(navigator.userAgent || "");
}

export function facingOf(device: Pick<MediaDeviceInfo, "label"> | null | undefined): CameraFacing | null {
  const label = device?.label || "";
  if (BACK_LABEL.test(label)) return "environment";
  if (FRONT_LABEL.test(label)) return "user";
  return null;
}

/** Entre as lentes de um lado, a principal; ultra-angular e tele só se não houver outra. */
export function pickCameraForFacing(
  devices: MediaDeviceInfo[],
  facing: CameraFacing,
): MediaDeviceInfo | null {
  const sameSide = devices.filter((d) => d.kind === "videoinput" && facingOf(d) === facing);
  return sameSide.find((d) => !EXTRA_LENS_LABEL.test(d.label)) ?? sameSide[0] ?? null;
}

export function readCameraFacing(): CameraFacing | null {
  try {
    const value = sessionStorage.getItem(FACING_KEY);
    return value === "user" || value === "environment" ? value : null;
  } catch {
    return null;
  }
}

function writeCameraFacing(facing: CameraFacing): void {
  try {
    sessionStorage.setItem(FACING_KEY, facing);
  } catch {
    // storage pode estar bloqueado
  }
}

function selfOf(meeting: RealtimeKitClient | null | undefined): CameraSelf | null {
  return (meeting?.self as unknown as CameraSelf) ?? null;
}

async function videoDevices(self: CameraSelf): Promise<MediaDeviceInfo[]> {
  try {
    return (await self.getVideoDevices?.()) ?? [];
  } catch {
    return [];
  }
}

/** Lembra o lado da câmera escolhido em Configurações › Vídeo ou no botão de virar. */
export function installCameraFacingCapture(meeting: RealtimeKitClient | null): () => void {
  const self = selfOf(meeting);
  if (!self?.on) return () => undefined;
  const onDeviceUpdate = ({ device }: { device?: MediaDeviceInfo }) => {
    if (device?.kind !== "videoinput") return;
    const facing = facingOf(device);
    if (facing) writeCameraFacing(facing);
  };
  self.on("deviceUpdate", onDeviceUpdate);
  return () => self.removeListener?.("deviceUpdate", onDeviceUpdate);
}

/**
 * No celular, quando a câmera é reaberta (volta do segundo plano, rede, nova
 * conexão), o navegador pode pegar a lente padrão do aparelho, que no Android é
 * a traseira. Volta para o lado escolhido; sem escolha, a frontal.
 */
export async function ensurePreferredCamera(meeting: RealtimeKitClient | null): Promise<void> {
  const self = selfOf(meeting);
  if (!self?.setDevice || !self.videoEnabled || !isMobileCameraBrowser()) return;
  const desired = readCameraFacing() ?? "user";
  const current = self.getCurrentDevices?.()?.video;
  if (facingOf(current) === desired) return;
  const target = pickCameraForFacing(await videoDevices(self), desired);
  if (!target || target.deviceId === current?.deviceId) return;
  try {
    await self.setDevice(target);
  } catch (err) {
    console.warn("Falha ao voltar para a câmera escolhida:", err);
  }
}

export async function hasBothCameraSides(meeting: RealtimeKitClient | null): Promise<boolean> {
  const self = selfOf(meeting);
  if (!self) return false;
  const devices = await videoDevices(self);
  return !!pickCameraForFacing(devices, "user") && !!pickCameraForFacing(devices, "environment");
}

/** @returns o lado que ficou ativo, ou null se não deu para trocar. */
export async function flipCamera(meeting: RealtimeKitClient | null): Promise<CameraFacing | null> {
  const self = selfOf(meeting);
  if (!self?.setDevice) return null;
  const current = self.getCurrentDevices?.()?.video;
  const currentFacing = facingOf(current) ?? readCameraFacing() ?? "user";
  const next: CameraFacing = currentFacing === "user" ? "environment" : "user";
  const target = pickCameraForFacing(await videoDevices(self), next);
  if (!target) return null;
  writeCameraFacing(next);
  await self.setDevice(target);
  return next;
}
